import React, { useState } from 'react';
import {
	Alert,
	Box,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { useCashTargets } from '../../hooks/useCashTargets';
import { putCashTarget, extractApiError } from '../../services';
import { ICashTarget } from '../../types';
import { formatCashAmount } from '../BranchCashSummary';

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const targetsInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Para qué sirven los objetivos? 🎯
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cada caja puede tener un monto objetivo por moneda. Cuando el saldo de caja chica supera ese objetivo, el sistema
			avisa que hay exceso y sugiere hacer un retiro al banco.
		</Typography>
		<Typography variant="body2">
			Así se asegura que la caja chica no acumule más efectivo del necesario para las operaciones del día a día.
		</Typography>
	</>
);

// ─── Fila editable ────────────────────────────────────────────────────────────

interface EditableRowProps {
	target: ICashTarget;
	onSaved: () => void;
}

function EditableTargetRow({ target, onSaved }: EditableRowProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const [editing, setEditing] = useState(false);
	const [value, setValue] = useState(String(target.targetAmount));
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSave = async () => {
		const num = parseFloat(value.replace(',', '.'));
		if (isNaN(num) || num < 0) {
			setError('Ingresá un monto válido');
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await putCashTarget({
				branch: target.branch,
				currency: target.currency,
				targetAmount: num,
				registeredBy: user?.email ?? 'unknown',
			});
			onSaved();
			setEditing(false);
		} catch (err) {
			setError(extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setValue(String(target.targetAmount));
		setError(null);
		setEditing(false);
	};

	return (
		<TableRow hover>
			<TableCell>{target.branch}</TableCell>
			<TableCell>{target.currency}</TableCell>
			<TableCell>
				{editing ? (
					<Box display="flex" alignItems="center" gap={1}>
						<TextField
							size="small"
							value={value}
							onChange={(e) => setValue(e.target.value)}
							sx={{ width: 130 }}
							autoFocus
							error={!!error}
							helperText={error}
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleSave();
								if (e.key === 'Escape') handleCancel();
							}}
						/>
						<IconButton size="small" onClick={handleSave} disabled={loading} color="success">
							{loading ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
						</IconButton>
						<IconButton size="small" onClick={handleCancel} disabled={loading}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
				) : (
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="body2" fontWeight={600}>
							{formatCashAmount(target.targetAmount, target.currency)}
						</Typography>
						<IconButton size="small" onClick={() => setEditing(true)}>
							<EditIcon sx={{ fontSize: 15 }} />
						</IconButton>
					</Box>
				)}
			</TableCell>
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{target.updatedBy ?? '—'}
				</Typography>
			</TableCell>
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{target.updatedAt
						? new Date(target.updatedAt).toLocaleDateString('es', {
								day: '2-digit',
								month: '2-digit',
								year: '2-digit',
						  })
						: '—'}
				</Typography>
			</TableCell>
		</TableRow>
	);
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Panel de configuración de objetivos de caja chica (superadmin).
 * Permite editar el monto objetivo por sucursal y moneda directamente en la tabla.
 */
export function CashTargetsPanel(): JSX.Element {
	const { targets: rawTargets, loading, error, refetch } = useCashTargets();
	const targets = Array.isArray(rawTargets) ? rawTargets : [];

	return (
		<>
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Box display="inline-flex" alignItems="center" gap={0.5} mb={1}>
					<Typography variant="subtitle1" fontWeight={600}>
						Objetivos de caja chica
					</Typography>
					<SectionInfoTooltip content={targetsInfoTooltip} />
				</Box>
				<Typography variant="body2" color="text.secondary">
					Hacé clic en el ícono de edición de la fila para modificar el objetivo de una caja/moneda.
				</Typography>
			</Paper>

			{loading ? (
				<Skeleton variant="rectangular" height={200} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : targets.length === 0 ? (
				<Alert severity="info">
					No hay objetivos configurados. Los objetivos se crean automáticamente al registrar el primer movimiento en
					cada caja.
				</Alert>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Sucursal</TableCell>
								<TableCell>Moneda</TableCell>
								<TableCell>Objetivo</TableCell>
								<TableCell>Editado por</TableCell>
								<TableCell>Fecha</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{targets.map((t) => (
								<EditableTargetRow key={`${t.branch}-${t.currency}`} target={t} onSaved={refetch} />
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</>
	);
}
